import jsPDF from "jspdf";

/**
 * Build a clean Spanish-language shopping list PDF using the QV brand (black + red).
 * Pure client-side, no backend call.
 *
 * @param {object} plan  - nutrition plan { proteins_foods, fats_foods, ... }
 * @param {string} clientName - athlete name (optional)
 * @returns {jsPDF} the document so caller can save/share.
 */
const CATEGORIES = [
  { key: "proteins_foods",   label: "PROTEINAS",     color: [225, 29, 46] },
  { key: "carbs_foods",      label: "CARBOHIDRATOS", color: [234, 179, 8] },
  { key: "fats_foods",       label: "GRASAS",        color: [34, 211, 238] },
  { key: "legumes_foods",    label: "LEGUMBRES",     color: [132, 204, 22] },
  { key: "vegetables_foods", label: "VERDURAS",      color: [34, 197, 94] },
  { key: "fruits_foods",     label: "FRUTAS",        color: [249, 115, 22] },
];

// Strip accents so the default jsPDF Helvetica font (Latin-1) renders cleanly.
const sanitize = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "");

export function buildShoppingPdf(plan, clientName = "") {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MARGIN = 14;
  const accent = [225, 29, 46];

  // Background black
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, W, H, "F");

  // Top accent bar
  doc.setFillColor(...accent);
  doc.rect(0, 0, W, 4, "F");

  // Header
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text("LISTA DE LA COMPRA", MARGIN, 22);

  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  doc.text(sanitize("PLATAFORMA RENDIMIENTO QV"), MARGIN, 28);

  // Meta line
  doc.setDrawColor(40, 40, 40);
  doc.line(MARGIN, 32, W - MARGIN, 32);
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  const dateStr = new Date().toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  doc.text(sanitize(dateStr.toUpperCase()), MARGIN, 38);
  if (clientName) {
    doc.setTextColor(225, 29, 46);
    doc.text(sanitize(clientName.toUpperCase()), W - MARGIN, 38, { align: "right" });
  }

  // Columns layout: 2 columns of categories
  const colWidth = (W - MARGIN * 2 - 6) / 2;
  let cursorY = 50;
  let column = 0;
  const colX = [MARGIN, MARGIN + colWidth + 6];
  const columnYs = [50, 50];

  const ensureSpace = (needed) => {
    if (columnYs[column] + needed > H - 18) {
      if (column === 0) {
        column = 1;
      } else {
        // new page
        doc.addPage();
        doc.setFillColor(10, 10, 10);
        doc.rect(0, 0, W, H, "F");
        doc.setFillColor(...accent);
        doc.rect(0, 0, W, 4, "F");
        column = 0;
        columnYs[0] = 20;
        columnYs[1] = 20;
      }
    }
  };

  CATEGORIES.forEach((cat) => {
    const items = (plan?.[cat.key] || []).filter(Boolean);
    if (items.length === 0) return;

    const blockHeight = 12 + Math.ceil(items.length) * 6 + 6;
    ensureSpace(blockHeight);
    const x = colX[column];
    let y = columnYs[column];

    // Category header
    doc.setFillColor(cat.color[0], cat.color[1], cat.color[2]);
    doc.rect(x, y, 3, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(cat.label, x + 6, y + 6);
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`${items.length} alim.`, x + colWidth, y + 6, { align: "right" });
    y += 12;

    // Items list with checkboxes
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    items.forEach((item) => {
      doc.setDrawColor(90, 90, 90);
      doc.rect(x, y - 3, 3, 3); // checkbox
      doc.setTextColor(225, 225, 225);
      const txt = sanitize(item);
      // Truncate if too long
      const maxWidth = colWidth - 8;
      let display = txt;
      if (doc.getTextWidth(display) > maxWidth) {
        while (display.length > 5 && doc.getTextWidth(display + "...") > maxWidth) {
          display = display.slice(0, -1);
        }
        display = display + "...";
      }
      doc.text(display, x + 5, y);
      y += 6;
    });
    y += 4;
    columnYs[column] = y;
  });

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.text(sanitize("Plan generado automaticamente desde tu perfil de nutricion. Personaliza con tu coach."), MARGIN, H - 8);
  doc.setTextColor(225, 29, 46);
  doc.text("@quiquevargas.coach", W - MARGIN, H - 8, { align: "right" });

  return doc;
}

export function buildShoppingText(plan) {
  const lines = ["🛒 *LISTA DE LA COMPRA QV*", ""];
  CATEGORIES.forEach((cat) => {
    const items = (plan?.[cat.key] || []).filter(Boolean);
    if (items.length === 0) return;
    lines.push(`*${cat.label}*`);
    items.forEach((it) => lines.push(`• ${it}`));
    lines.push("");
  });
  lines.push("_Plataforma Rendimiento QV_");
  return lines.join("\n");
}
